import { isProduceRacVendorAssessmentLayout } from "@/utils/productSpecificationTypeUtils";
import {
  requiresProduceRacProductInformationPricingFields,
} from "@/app/(authorised)/(sidebar)/create-product/shopping-list-management/utils/opportunityBuyUtils";

/** Permission key shared by Produce RAC - TJ Label and Produce RAC - Branded in MainDetails.json */
export const PRODUCE_RAC_MAIN_DETAILS_PERMISSION_KEY = "Produce RAC";

/** True when Main Details should use the Produce RAC product-details datapoint layout. */
export function isProduceRacMainDetailsLayout(
  productSpecificationType?: string | null
): boolean {
  return isProduceRacVendorAssessmentLayout(productSpecificationType);
}

/** Multi-pack fields are not part of the Produce RAC Main Details datapoints spec. */
export function shouldShowMainDetailsMultiPackFields(
  productSpecificationType?: string | null
): boolean {
  return !isProduceRacMainDetailsLayout(productSpecificationType);
}

/**
 * True when Retail Price, Product Size, and Case Pack should appear in Main Details:
 * Produce RAC - TJ Label, or Produce RAC - Branded with Opportunity Buy ≠ Yes.
 */
export function shouldShowProduceRacMainDetailsPricingFields(
  productSpecificationType?: string | null,
  opportunityBuy?: string | null
): boolean {
  return requiresProduceRacProductInformationPricingFields(
    productSpecificationType,
    opportunityBuy
  );
}

/** Normalize retail price values from list/API (number or string) for form display. */
export function formatRetailPriceFromSource(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  if (typeof value === "number" && value === 0) {
    return "";
  }
  return String(value).trim();
}

/** Normalize text pricing-related values from list/API for form display. */
export function formatPricingTextFromSource(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).trim();
}

/** Resolve opportunityBuy from GET/save API shapes (nested or top-level). */
export function resolveOpportunityBuyFromApiResponse(response: unknown): string {
  if (!response || typeof response !== "object") {
    return "";
  }

  const data = response as Record<string, unknown>;
  const specInfo = data.productSpecificationInformation;

  const raw =
    (specInfo &&
      typeof specInfo === "object" &&
      (specInfo as Record<string, unknown>).opportunityBuy) ??
    data.opportunityBuy;

  return formatPricingTextFromSource(raw);
}

/**
 * Resolve pricing fields from Main Details GET (flat) or PUT body (nested under productRelatedInformation).
 *
 * API names: retailPrice, size, packSize
 * Form names: retailPrice, productSize, casePack
 */
export function resolveProduceRacPricingFieldsFromApiResponse(response: unknown): {
  retailPrice: string;
  productSize: string;
  casePack: string;
} {
  if (!response || typeof response !== "object") {
    return { retailPrice: "", productSize: "", casePack: "" };
  }

  const data = response as Record<string, unknown>;
  const pri =
    data.productRelatedInformation &&
    typeof data.productRelatedInformation === "object"
      ? (data.productRelatedInformation as Record<string, unknown>)
      : undefined;

  // GET = top-level; PUT payload mirror = nested under productRelatedInformation
  const retailPrice = formatRetailPriceFromSource(data.retailPrice ?? pri?.retailPrice);
  const productSize = formatPricingTextFromSource(data.size ?? pri?.size);
  const casePack = formatPricingTextFromSource(data.packSize ?? pri?.packSize);

  return { retailPrice, productSize, casePack };
}

/** Map form retailPrice to API (number when numeric, e.g. 456.87; null when empty). */
export function retailPriceToApiValue(value: unknown): number | null {
  const formatted = formatRetailPriceFromSource(value);
  if (!formatted) {
    return null;
  }
  const num = Number(formatted);
  return Number.isNaN(num) ? null : num;
}

/** Map form case pack to API (number when numeric, e.g. 345; null when empty). */
export function packSizeToApiValue(value: unknown): number | null {
  const formatted = formatPricingTextFromSource(value);
  if (!formatted) {
    return null;
  }
  const num = Number(formatted);
  return Number.isNaN(num) ? null : num;
}

/** Build productRelatedInformation pricing keys for Main Details PUT payload. */
export function resolveMainDetailsPricingFieldsForSave(
  formPricing: {
    retailPrice?: string;
    productSize?: string;
    casePack?: string;
  },
  apiData?: unknown
): { retailPrice: number | null; size: string; packSize: number | null } {
  const fromApi = resolveProduceRacPricingFieldsFromApiResponse(apiData);

  return {
    retailPrice: retailPriceToApiValue(formPricing.retailPrice || fromApi.retailPrice),
    size: String(formPricing.productSize || fromApi.productSize || "").trim(),
    packSize: packSizeToApiValue(formPricing.casePack || fromApi.casePack),
  };
}

/** Resolve pricing fields from active-projects list row / idea payload shapes. */
/** Normalize API certifiedOrganic values to form dropdown values (Yes / No). */
export function normalizeCertifiedOrganicFromApi(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized === "yes" || normalized === "true") {
    return "Yes";
  }
  if (normalized === "no" || normalized === "false") {
    return "No";
  }

  return String(value).trim();
}

/** Resolve certifiedOrganic from GET/save API shapes (nested or top-level). */
export function resolveCertifiedOrganicFromApiResponse(response: unknown): string {
  if (!response || typeof response !== "object") {
    return "";
  }

  const data = response as Record<string, unknown>;
  const vendorRelated = data.vendorRelatedInformation;

  const raw =
    (vendorRelated &&
      typeof vendorRelated === "object" &&
      (vendorRelated as Record<string, unknown>).certifiedOrganic) ??
    data.certifiedOrganic;

  return normalizeCertifiedOrganicFromApi(raw);
}

/** Map form certifiedOrganic to API payload (Yes / No or null). */
export function certifiedOrganicToApiValue(formValue: unknown): string | null {
  const normalized = normalizeCertifiedOrganicFromApi(formValue);
  if (!normalized) {
    return null;
  }
  return normalized;
}
